/* eslint-disable no-undef */
import React from 'react'
import Head from 'next/head'
import { useEffect } from 'react'
// eslint-disable-next-line no-unused-vars
import styles from '../styles/global.scss'
import dynamic from 'next/dynamic'
import axios from '/components/configs/axios'
import { useRouter } from 'next/router'
import { ResponsiveProvider } from '../providers/ResponsiveContext'
import { flare } from '@flareapp/js'
import { FlareErrorBoundary } from '@flareapp/react'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/nl'
import moment from 'moment'

// Only enable Flare in production, we don't want to waste your quota while you're developing:
if (process.env.NODE_ENV === 'production') {
  flare.light('dXZNZtZBqlu4at3vdmFeC9iJlyQooaFk')
}

const Analytics = dynamic(() => import('../components/pages/app/Analytics'), {
  ssr: false
})

const Providers = dynamic(() => import('../providers/MainProvider'), {
  ssr: true
})

const LoadingPage = dynamic(() => import('../components/templates/LoadingPage'))

const MyApp = ({ Component, pageProps }) => {
  const router = useRouter()
  const { maintenance } = pageProps

  useEffect(() => {
    if (moment) {
      moment.locale(router.locale)
    }
  }, [router.locale])

  return (
    <>
      <Head>
        <link rel="dns-prefetch" href={process.env.NEXT_PUBLIC_ASSETS_URL} />
        <link rel="dns-prefetch" href={process.env.NEXT_PUBLIC_API_URL} />
        <link
          rel="preload"
          href="/fonts/bentonsans-medium.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />

        <link
          rel="preload"
          href="/fonts/bentonsans-regular.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/bentonsans-light.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/freightbigpro-medium-italic.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/freightbigpro-medium.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />

        <link rel="preconnect" href={process.env.NEXT_PUBLIC_ASSETS_URL} />
        <link rel="preconnect" href={process.env.NEXT_PUBLIC_API_URL} />
        <link rel="preconnect" href="https://imagedelivery.net" />
        <link rel="icon" href="/manifest/favicon.ico" />

        <link
          rel="manifest"
          href="/manifest/manifest.json"
          crossOrigin="use-credentials"
        />
        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content={
            typeof window !== 'undefined' ? window.location.href : undefined
          }
        />

        <meta
          name="msapplication-config"
          content="/manifest/browserconfig.xml"
        />
      </Head>

      <Analytics />

      {/* Wrap the entire app with Flare Error Boundary */}
      <FlareErrorBoundary>
        {/* Wrap the existing Providers with ResponsiveProvider */}
        <ResponsiveProvider initialState={pageProps.__responsiveState}>
          <Providers>
            {maintenance ? (
              <LoadingPage
                title="Maintenance in Progress"
                subtitle="We're working hard to improve your experience. Please bear with us as we enhance our site to serve you better. We'll be back online shortly!"
                spinner={false}
              />
            ) : (
              <Component {...pageProps} />
            )}
          </Providers>
        </ResponsiveProvider>
      </FlareErrorBoundary>
    </>
  )
}

// Add getInitialProps to fetch maintenance status on the server
MyApp.getInitialProps = async (appContext) => {
  // Call the page's getInitialProps if it exists
  const appProps = appContext.Component.getInitialProps
    ? await appContext.Component.getInitialProps(appContext.ctx)
    : {}

  // Fetch maintenance status on the server
  let maintenance = false
  try {
    // Use absolute URL for server-side requests
    const baseURL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000'
    const instance = appContext.ctx.req
      ? axios.create({ baseURL }) // Server-side request
      : axios // Client-side request

    const response = await instance.get('maintenance')
    maintenance = response.data.isUnderMaintenance
  } catch (error) {
    console.error('Failed to fetch maintenance status:', error)
    // Default to false if request fails
    maintenance = false
  }

  return {
    pageProps: {
      ...appProps,
      maintenance,
      __responsiveState: appProps.__responsiveState || null
    }
  }
}

export default MyApp
